import { FC } from 'react';
import { IProviderItemView } from 'store/providers/providerTypes';
import styled from 'styled-components';
import { black } from 'utils/style/colors';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useRef, useState } from 'react';
import Icon from 'components/Icon';
import { navigate } from '@reach/router';
import PageCKEditor from 'components/FileManager/ItemsViewer/Quill/PageCkEditor';

export const fetchDocumentContentViaURL = async (url: string) => {
  try {
    const res = await fetch(url);
    const formatedResponse = await res.text();
    return formatedResponse;
  } catch (error) {
    console.error('Fetch error:', error);
    return;
  }
};

const ContextItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
`;

const ContextItemRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ContextItemHeader = styled.h2`
  font-weight: bold;
  font-size: 1.6rem;
  margin: 1.5rem auto 2.5rem;
  text-align: center;
  width: 100%;
`;

const ContextItemContent = styled.div<{
  isExtended?: boolean;
  isMoreDisabled?: boolean;
}>`
  max-height: ${(props) =>
    props.isMoreDisabled || props.isExtended ? 'none' : '7.5rem'};
  overflow: hidden;
  width: 100%;
  transition: all ease-out 0.5s;
`;

const More = styled.a<{ isExtended?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease-in;
  font-weight: bold;
  cursor: pointer;
  margin-top: 1rem;
  color: ${black};

  &:hover {
    transform: scale(1.15)
      ${(props) => (props.isExtended ? 'translateY(-2px)' : 'translateY(2px)')};

    & > i {
      transform: ${(props) =>
        props.isExtended ? 'translateY(-4px)' : 'translateY(4px)'};
    }
  }
`;

interface IProps {
  items: IProviderItemView[];
}

const ExpandableText: FC<IProps> = ({ items }) => {
  const { t } = useTranslation('landingPage');

  const memoizedItems = useMemo(() => items, [items]);

  const [fetchedContent, setFetchedContent] = useState<Record<number, string>>(
    {}
  );
  const [extendedIdList, setExtendedIdList] = useState<Array<number>>([]);
  const [hasOverflowByItemId, setHasOverflowByItemId] = useState<
    Record<number, boolean>
  >({});
  const contentRefs = useRef<Record<number, HTMLDivElement | null>>({});

  useEffect(() => {
    const fetchAllItemsContent = async () => {
      const contents: Record<number, string> = {};
      for (const item of memoizedItems) {
        const fetchedData = await fetchDocumentContentViaURL(
          item?.document?.url
        );
        contents[Number(item.id)] = fetchedData ?? '';
      }
      setFetchedContent(contents);
    };
    fetchAllItemsContent();
  }, [memoizedItems]);

  useEffect(() => {
    const calculateOverflow = () => {
      const rootFontSize = Number.parseFloat(
        getComputedStyle(document.documentElement).fontSize
      );
      const collapsedMaxHeight = 7.5 * rootFontSize;
      const next: Record<number, boolean> = {};

      items.forEach((item) => {
        const id = Number(item.id);
        const element = contentRefs.current[id];
        next[id] = !!element && element.scrollHeight > collapsedMaxHeight + 1;
      });

      setHasOverflowByItemId(next);
    };

    const frameId = requestAnimationFrame(calculateOverflow);
    window.addEventListener('resize', calculateOverflow);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', calculateOverflow);
    };
  }, [items, fetchedContent]);

  const handleClickOnMore = (item: IProviderItemView) => {
    if (item.moreInfoUrl) navigate(item.moreInfoUrl);
    else if (!extendedIdList.includes(Number(item.id))) {
      setExtendedIdList((prevState) => [...prevState, Number(item.id)]);
    } else {
      setExtendedIdList((prevState) => [
        ...prevState.filter((itemId) => itemId !== Number(item.id)),
      ]);
    }
  };

  return (
    <ContextItemContainer>
      {items.map((contextItem: IProviderItemView) => {
        const itemId = Number(contextItem.id);
        const isExtended = extendedIdList.includes(itemId);
        const disableMore = !hasOverflowByItemId[itemId];
        return (
          <ContextItemRow key={contextItem.id}>
            <ContextItemHeader>
              {contextItem?.name || contextItem?.title}
            </ContextItemHeader>
            <ContextItemContent
              ref={(element: HTMLDivElement | null) => {
                contentRefs.current[itemId] = element;
              }}
              isExtended={isExtended}
              isMoreDisabled={disableMore}
            >
              <PageCKEditor
                editing={false}
                saving={false}
                content={fetchedContent[itemId] || ''}
                updateContent={() => {
                  /* view-only */
                }}
              />
            </ContextItemContent>

            {!disableMore && (
              <More
                isExtended={isExtended}
                onClick={() => handleClickOnMore(contextItem)}
              >
                <div>{isExtended ? t('less') : t('more')}</div>
                <Icon
                  type={isExtended ? 'chevrons-up' : 'chevrons-down'}
                  color={black}
                />
              </More>
            )}
          </ContextItemRow>
        );
      })}
    </ContextItemContainer>
  );
};

export default ExpandableText;
